import * as R from 'ramda';
import getSeries from '../factories/series';
import { BAR, ROW, TIMELINE } from '../constants';
import { hasFocus } from './utils';
import { dimensionValueDisplay, parseDisplay } from '../dimension-utils';

const sampleFocusTypes = { [BAR]: BAR, [ROW]: ROW, [TIMELINE]: TIMELINE };

const focusComparator = (op1, op2) => op1.value === op2.value;

const focus = (data, type, dimension, _display) => {
  const display = parseDisplay(_display);
  const id = R.prop('id', dimension);
  switch (type) {
    case 'RowChart':
    case 'BarChart':
    case 'HorizontalSymbolChart':
    case 'VerticalSymbolChart':
    case 'StackedBarChart':
    case 'StackedRowChart':
      return R.pipe(
        getSeries,
        R.pathOr([], [0, 'datapoints']),
        R.map(dp => ({ label: dp.category, value: dp.key })),
      )({ data, type, options: { display }, singularity: dimension });
    case 'TimelineChart':
      return R.pipe(
        getSeries,
        R.map(serie => ({ label: serie.category, value: serie.key })),
      )({ data, type, options: { display }, singularity: dimension });
    case 'ScatterChart':
      if (R.isNil(id)) return [];
      return R.pipe(
        R.propOr([], 'dimensions'),
        R.reduce((acc, dim) => {
          if (dim.header || dim.id === id) {
            return acc;
          }
          const dimOptions = R.map(
            val => ({
              label: `${dimensionValueDisplay(display)(dim)} - ${dimensionValueDisplay(display)(val)}`,
              options: { dimensionId: dim.id, dimensionValueId: val.id },
              value: `${dim.id}-${val.id}`,
            }),
            R.propOr([], 'values', dim),
          );
          return R.concat(acc, dimOptions);
        }, []),
      )(data);
    default:
      return [];
  }
};

export const focusOptions = (props, state, otherFocus, singularity) => {
  if (!hasFocus(props.type)) {
    return [];
  }
  const options = focus(props.data, props.type, singularity, props.display);
  return R.differenceWith(focusComparator, options, R.prop(otherFocus, state));
};

export const onChangeFocusSelection = _value => {
  const value = R.isNil(_value) ? [] : _value;
  return R.is(Array, value) ? value : [value];
};

const toKey = () => R.prop('value');

const filterFocus = (focus, keyedOptions, type) =>
  R.reduce(
    (acc, entry) => {
      const entryKey = toKey(type)(entry);
      if (R.has(entryKey, keyedOptions)) {
        return R.append(R.prop(entryKey, keyedOptions), acc);
      }
      return acc;
    },
    [],
    focus,
  );

export const focusStateFromNewProps = ({ data, display, type }, singularity, state) => {
  const highlightState = R.propOr([], 'highlight', state);
  const baselineState = R.propOr([], 'baseline', state);
  const options = focus(data, type, singularity, display);
  const keyedOptions = R.indexBy(toKey(type), options);
  return {
    baseline: filterFocus(baselineState, keyedOptions, R.prop('type', state)),
    highlight: filterFocus(highlightState, keyedOptions, R.prop('type', state)),
  };
};

/*
  This props/state parser which will be exposed in highlight/baseline properties,
  only deals for Bar and Row Chart (no chart dimension singularity involved).
  For other chart types, their own chart dimension singularity parser will handle focus management.
*/

export const sampleFocusStateFromNewProps = (props, state) => {
  if (!R.has(props.type, sampleFocusTypes) || R.isNil(props.data)) {
    return {}; //no changes
  }
  return focusStateFromNewProps(props, null, state);
};
